import React, { useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Box, Button } from "@mui/material";
import { publishableKey } from "../utils/stripe-config";
import { formatPrice } from "../utils/utils";

const stripePromise = loadStripe(publishableKey, {
  developerTools: { assistant: { enabled: false } },
});

interface CreditCheckoutFormProps {
  clientSecret: string;
  customerSessionClientSecret?: string;
  amount: number;
  credits: number;
  currency?: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const CheckoutFormInner = ({
  amount,
  credits,
  currency = "usd",
  onSuccess,
  onError,
}: Omit<CreditCheckoutFormProps, "clientSecret">) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      setIsProcessing(false);

      if (error) {
        console.error("[CreditCheckoutForm] Payment error:", error);
        onError(error.message || "Payment failed");
      } else if (paymentIntent?.status === "succeeded") {
        onSuccess();
      } else if (paymentIntent?.status === "processing") {
        onError(
          "Payment is processing. Credits will be added once confirmed."
        );
      } else {
        console.error(
          "[CreditCheckoutForm] Unexpected payment status:",
          paymentIntent?.status
        );
        onError("Payment could not be completed. Please try again.");
      }
    } catch (err) {
      console.error("[CreditCheckoutForm] Unexpected error:", err);
      onError("An unexpected error occurred");
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <Box sx={{ height: "16px" }} />
      <Button
        type="submit"
        variant="contained"
        disabled={!stripe || isProcessing}
        fullWidth
        sx={{
          fontWeight: 700,
          background: "blue",
          textTransform: "none",
          "&:disabled": {
            opacity: 0.6,
          },
        }}
      >
        {isProcessing
          ? "Processing..."
          : `Pay ${formatPrice(amount, currency, true)} for ${credits} Credits`}
      </Button>
    </form>
  );
};

export const CreditCheckoutForm = ({
  clientSecret,
  customerSessionClientSecret,
  ...props
}: CreditCheckoutFormProps) => {
  const options = {
    clientSecret,
    ...(customerSessionClientSecret && { customerSessionClientSecret }),
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: "#000000",
        colorText: "#111827",
        colorDanger: "#b42318",
        colorBackground: "#ffffff",
        borderRadius: "12px",
        fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
        spacingUnit: "8px",
      },
      rules: {
        ".Input": {
          borderColor: "#dfe3eb",
          boxShadow: "none",
        },
        ".Tab, .Block": {
          borderColor: "#dfe3eb",
        },
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutFormInner {...props} />
    </Elements>
  );
};
